<script>
import { useAlert } from 'dashboard/composables';
import { mapActions, mapGetters } from 'vuex';
import BaseModal from 'dashboard/components/dahreply/BaseModal.vue';
import {
  apiErrorCode,
  apiErrorDetail,
  apiErrorMessage,
} from '../../../../api/coLiving/errors';
import { FIELD_CLASS, SELECT_CLASS } from '../constants';
import { listTenants } from '../../../../api/coLiving/tenants';
import { hasRooms } from '../residentsForm';
import {
  PHOTO_ACCEPT,
  DEFAULT_MAX_IMAGES,
  DEFAULT_MAX_IMAGE_MB,
  photoProblem,
} from '../photos';
import { emptyTicket, validateTicket, ticketPayload } from '../ticketForm';
import { MANUAL_SOURCES, LOCATION_TYPES, SEVERITIES } from '../ticketHelpers';
import FormField from './FormField.vue';

const TENANT_PAGE_SIZE = 100;

export default {
  name: 'NewTicketModal',
  components: { BaseModal, FormField },
  emits: ['close', 'created'],
  data() {
    return {
      fieldClass: FIELD_CLASS,
      selectClass: SELECT_CLASS,
      sources: MANUAL_SOURCES,
      locationTypes: LOCATION_TYPES,
      severities: SEVERITIES,
      form: emptyTicket(),
      units: [],
      tenants: [],
      errors: {},
      duplicate: null,
      isSubmitting: false,
      photoAccept: PHOTO_ACCEPT,
      photos: [],
      photoError: '',
    };
  },
  computed: {
    ...mapGetters('coLivingCondos', { condos: 'getActiveCondos' }),
    ...mapGetters('coLivingCatalog', { categories: 'getActiveCategories' }),
    ...mapGetters('coLivingConfig', { settings: 'getSettings' }),
    maxImages() {
      return (this.settings && this.settings.max_images) || DEFAULT_MAX_IMAGES;
    },
    maxImageMb() {
      return (
        (this.settings && this.settings.max_image_mb) || DEFAULT_MAX_IMAGE_MB
      );
    },
    selectedUnit() {
      return this.units.find(unit => unit.id === this.form.unit_id);
    },
    showRoom() {
      return (
        !this.form.common_area &&
        !!this.selectedUnit &&
        hasRooms(this.selectedUnit.kind)
      );
    },
  },
  watch: {
    'form.condo_id'(condoId) {
      this.form.unit_id = '';
      this.form.room_id = '';
      this.form.tenant_id = '';
      this.loadUnits(condoId);
    },
    'form.unit_id'(unitId) {
      this.form.room_id = '';
      this.form.tenant_id = '';
      this.loadTenants(unitId);
    },
    'form.common_area'(value) {
      if (value) {
        this.form.unit_id = '';
        this.form.location_type = 'common_area';
      }
    },
  },
  mounted() {
    this.fetchCondos();
    this.fetchCategories();
    this.fetchSettings();
  },
  methods: {
    ...mapActions('coLivingCondos', ['fetchCondos']),
    ...mapActions('coLivingCatalog', ['fetchCategories']),
    ...mapActions('coLivingResidents', ['fetchAllUnits']),
    ...mapActions('coLivingTickets', ['createTicket', 'addPhotos']),
    ...mapActions('coLivingConfig', ['fetchSettings']),
    async loadUnits(condoId) {
      this.units = [];
      if (!condoId) return;
      try {
        this.units = await this.fetchAllUnits(condoId);
      } catch (err) {
        useAlert(apiErrorMessage(this, err, 'CO_LIVING.COMMON.LOAD_FAILED'));
      }
    },
    // Tenants of the unit, to name the requester. The API is called directly:
    // the residents store holds the Residents page's own list.
    async loadTenants(unitId) {
      this.tenants = [];
      if (!unitId) return;
      try {
        const { data } = await listTenants({
          unit_id: unitId,
          page_size: TENANT_PAGE_SIZE,
        });
        this.tenants = data.results;
      } catch (err) {
        this.tenants = [];
      }
    },
    errorText(field) {
      return this.errors[field]
        ? this.$t(`CO_LIVING.TICKETS.NEW.ERRORS.${this.errors[field]}`)
        : '';
    },
    async onSubmit() {
      this.errors = validateTicket(this.form);
      this.duplicate = null;
      if (Object.keys(this.errors).length) return;
      this.isSubmitting = true;
      try {
        const result = await this.createTicket(
          ticketPayload(this.form, this.selectedUnit)
        );
        useAlert(
          this.$t(`CO_LIVING.TICKETS.NEW.RESULT.${result.result.toUpperCase()}`)
        );
        await this.uploadPhotos(result.ticket);
        this.$emit('created', result.ticket);
        this.$emit('close');
      } catch (err) {
        const code = apiErrorCode(err);
        if (code === 'duplicate_open_ticket') {
          this.duplicate = apiErrorDetail(err).ticket || null;
          // The form is taller than a laptop screen; bring the notice into view.
          this.$nextTick(() => {
            const notice = this.$refs.duplicateNotice;
            if (notice && notice.scrollIntoView) {
              notice.scrollIntoView({ block: 'nearest' });
            }
          });
        } else if (code === 'invalid_location') {
          this.errors = { unit_id: 'LOCATION' };
        } else {
          useAlert(apiErrorMessage(this, err, 'CO_LIVING.TICKETS.NEW.FAILED'));
        }
      } finally {
        this.isSubmitting = false;
      }
    },
    // The create call has no photo field: photos go to the new ticket after it
    // exists. If they fail the ticket stays (it opens next), and the admin can
    // add them again from its Photos card.
    async uploadPhotos(ticket) {
      if (!this.photos.length || !ticket) return;
      try {
        await this.addPhotos({ id: ticket.id, files: this.photos });
      } catch (err) {
        useAlert(
          `${this.$t('CO_LIVING.TICKETS.NEW.PHOTOS_FAILED')} ${apiErrorMessage(
            this,
            err,
            'CO_LIVING.TICKETS.PHOTOS.ADD_FAILED'
          )}`
        );
      }
    },
    onPhotos(event) {
      const files = Array.from(event.target.files || []);
      this.photos = [];
      this.photoError = '';
      const bad = files.find(file => photoProblem(file, this.maxImageMb));
      if (bad) {
        this.photoError = this.$t(
          `CO_LIVING.TICKETS.PHOTOS.PROBLEMS.${photoProblem(
            bad,
            this.maxImageMb
          )}`,
          { name: bad.name, mb: this.maxImageMb }
        );
        event.target.value = '';
        return;
      }
      if (files.length > this.maxImages) {
        this.photoError = this.$t('CO_LIVING.TICKETS.PHOTOS.PROBLEMS.LIMIT', {
          max: this.maxImages,
        });
        event.target.value = '';
        return;
      }
      this.photos = files;
    },
    openDuplicate() {
      this.$emit('created', this.duplicate);
      this.$emit('close');
    },
  },
};
</script>

<template>
  <BaseModal size="large" @close="$emit('close')">
    <template #title>
      {{ $t('CO_LIVING.TICKETS.NEW.TITLE') }}
    </template>

    <form
      class="grid grid-cols-1 md:grid-cols-2 gap-4"
      data-testid="co-living-new-ticket-form"
      @submit.prevent="onSubmit"
    >
      <FormField
        :label="$t('CO_LIVING.TICKETS.NEW.SOURCE')"
        for-id="co-living-new-ticket-source"
        :error="errorText('source')"
      >
        <select
          id="co-living-new-ticket-source"
          v-model="form.source"
          :class="[selectClass, errors.source ? 'border-red-500' : '']"
          data-testid="co-living-new-ticket-source"
        >
          <option value="" disabled>
            {{ $t('CO_LIVING.TICKETS.NEW.SOURCE_PLACEHOLDER') }}
          </option>
          <option v-for="source in sources" :key="source" :value="source">
            {{ $t(`CO_LIVING.TICKETS.SOURCES.${source.toUpperCase()}`) }}
          </option>
        </select>
      </FormField>

      <FormField
        :label="$t('CO_LIVING.TICKETS.NEW.CATEGORY')"
        for-id="co-living-new-ticket-category"
        :error="errorText('category')"
      >
        <select
          id="co-living-new-ticket-category"
          v-model="form.category"
          :class="[selectClass, errors.category ? 'border-red-500' : '']"
          data-testid="co-living-new-ticket-category"
        >
          <option value="" disabled>
            {{ $t('CO_LIVING.TICKETS.NEW.CATEGORY_PLACEHOLDER') }}
          </option>
          <option v-for="c in categories" :key="c.key" :value="c.key">
            {{ c.name }}
          </option>
        </select>
      </FormField>

      <FormField
        :label="$t('CO_LIVING.TICKETS.NEW.CONDO')"
        for-id="co-living-new-ticket-condo"
        :error="errorText('condo_id')"
      >
        <select
          id="co-living-new-ticket-condo"
          v-model="form.condo_id"
          :class="[selectClass, errors.condo_id ? 'border-red-500' : '']"
          data-testid="co-living-new-ticket-condo"
        >
          <option value="" disabled>
            {{ $t('CO_LIVING.TICKETS.NEW.CONDO_PLACEHOLDER') }}
          </option>
          <option v-for="condo in condos" :key="condo.id" :value="condo.id">
            {{ condo.name }}
          </option>
        </select>
      </FormField>

      <label
        class="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200 md:mt-6"
      >
        <input
          v-model="form.common_area"
          type="checkbox"
          class="mb-0"
          data-testid="co-living-new-ticket-common-area"
        />
        {{ $t('CO_LIVING.TICKETS.NEW.COMMON_AREA') }}
      </label>

      <FormField
        v-if="!form.common_area"
        :label="$t('CO_LIVING.TICKETS.NEW.UNIT')"
        for-id="co-living-new-ticket-unit"
        :error="errorText('unit_id')"
      >
        <select
          id="co-living-new-ticket-unit"
          v-model="form.unit_id"
          :disabled="!form.condo_id"
          :class="[selectClass, errors.unit_id ? 'border-red-500' : '']"
          data-testid="co-living-new-ticket-unit"
        >
          <option value="" disabled>
            {{ $t('CO_LIVING.TICKETS.NEW.UNIT_PLACEHOLDER') }}
          </option>
          <option v-for="unit in units" :key="unit.id" :value="unit.id">
            {{ unit.name }}
          </option>
        </select>
      </FormField>

      <FormField
        v-if="showRoom"
        :label="$t('CO_LIVING.TICKETS.NEW.ROOM')"
        for-id="co-living-new-ticket-room"
        :hint="$t('CO_LIVING.TICKETS.NEW.ROOM_HINT')"
      >
        <select
          id="co-living-new-ticket-room"
          v-model="form.room_id"
          :class="selectClass"
          data-testid="co-living-new-ticket-room"
        >
          <option value="">{{ $t('CO_LIVING.TICKETS.NEW.WHOLE_UNIT') }}</option>
          <option
            v-for="room in selectedUnit.rooms"
            :key="room.id"
            :value="room.id"
          >
            {{ room.name }}
          </option>
        </select>
      </FormField>

      <FormField
        v-if="form.unit_id && !form.common_area"
        :label="$t('CO_LIVING.TICKETS.NEW.TENANT')"
        for-id="co-living-new-ticket-tenant"
      >
        <select
          id="co-living-new-ticket-tenant"
          v-model="form.tenant_id"
          :class="selectClass"
          data-testid="co-living-new-ticket-tenant"
        >
          <option value="">{{ $t('CO_LIVING.TICKETS.NEW.NO_TENANT') }}</option>
          <option v-for="tenant in tenants" :key="tenant.id" :value="tenant.id">
            {{ tenant.name }}
          </option>
        </select>
      </FormField>

      <FormField
        :label="$t('CO_LIVING.TICKETS.NEW.SEVERITY')"
        for-id="co-living-new-ticket-severity"
      >
        <select
          id="co-living-new-ticket-severity"
          v-model="form.severity"
          :class="selectClass"
          data-testid="co-living-new-ticket-severity"
        >
          <option value="">
            {{ $t('CO_LIVING.TICKETS.NEW.CATEGORY_DEFAULT') }}
          </option>
          <option v-for="s in severities" :key="s" :value="s">
            {{ $t(`CO_LIVING.CATEGORIES.SEVERITIES.${s.toUpperCase()}`) }}
          </option>
        </select>
      </FormField>

      <FormField
        :label="$t('CO_LIVING.TICKETS.NEW.LOCATION_TYPE')"
        for-id="co-living-new-ticket-location-type"
      >
        <select
          id="co-living-new-ticket-location-type"
          v-model="form.location_type"
          :class="selectClass"
          data-testid="co-living-new-ticket-location-type"
        >
          <option value="">{{ $t('CO_LIVING.COMMON.NONE') }}</option>
          <option v-for="type in locationTypes" :key="type" :value="type">
            {{ $t(`CO_LIVING.TICKETS.LOCATION_TYPES.${type.toUpperCase()}`) }}
          </option>
        </select>
      </FormField>

      <div class="md:col-span-2">
        <FormField
          :label="$t('CO_LIVING.TICKETS.NEW.DESCRIPTION')"
          for-id="co-living-new-ticket-description"
          :error="errorText('description')"
          :hint="$t('CO_LIVING.TICKETS.NEW.DESCRIPTION_HINT')"
        >
          <textarea
            id="co-living-new-ticket-description"
            v-model="form.description"
            rows="4"
            maxlength="4000"
            :class="[fieldClass, errors.description ? 'border-red-500' : '']"
            data-testid="co-living-new-ticket-description"
          />
        </FormField>
      </div>

      <label
        class="md:col-span-2 flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200"
      >
        <input
          v-model="form.emergency"
          type="checkbox"
          class="mb-0"
          data-testid="co-living-new-ticket-emergency"
        />
        {{ $t('CO_LIVING.TICKETS.NEW.EMERGENCY') }}
      </label>

      <div class="md:col-span-2">
        <label
          for="co-living-new-ticket-photos"
          class="block mb-1 text-sm text-slate-700 dark:text-slate-200"
        >
          {{ $t('CO_LIVING.TICKETS.NEW.PHOTOS') }}
        </label>
        <input
          id="co-living-new-ticket-photos"
          type="file"
          multiple
          :accept="photoAccept"
          class="block w-full text-sm text-slate-700 dark:text-slate-200"
          data-testid="co-living-new-ticket-photos"
          @change="onPhotos"
        />
        <p
          v-if="photoError"
          class="mt-1 text-xs text-red-500"
          data-testid="co-living-new-ticket-photo-error"
        >
          {{ photoError }}
        </p>
        <p v-else class="mt-1 text-xs text-slate-500 dark:text-slate-400">
          {{
            $t('CO_LIVING.TICKETS.PHOTOS.LIMITS', {
              max: maxImages,
              mb: maxImageMb,
            })
          }}
        </p>
      </div>

      <div
        v-if="duplicate"
        ref="duplicateNotice"
        class="md:col-span-2 p-3 rounded bg-amber-50 dark:bg-amber-900/30 text-sm text-amber-800 dark:text-amber-200"
        data-testid="co-living-duplicate-ticket"
      >
        {{ $t('CO_LIVING.TICKETS.NEW.DUPLICATE', { id: duplicate.id }) }}
        <woot-button
          variant="clear"
          size="small"
          type="button"
          data-testid="co-living-open-duplicate"
          @click="openDuplicate"
        >
          {{ $t('CO_LIVING.TICKETS.NEW.OPEN_EXISTING') }}
        </woot-button>
      </div>

      <div class="md:col-span-2 flex justify-end gap-3">
        <woot-button variant="clear" type="button" @click="$emit('close')">
          {{ $t('CO_LIVING.COMMON.CANCEL') }}
        </woot-button>
        <woot-button
          type="submit"
          :is-loading="isSubmitting"
          data-testid="co-living-new-ticket-save"
        >
          {{ $t('CO_LIVING.TICKETS.NEW.SUBMIT') }}
        </woot-button>
      </div>
    </form>
  </BaseModal>
</template>
